import {ceilToStep, getNumberOfDecimals, roundToDecimalsWithPow} from '../../utils/math'

import {LoadBalanceConfiguration} from './types'

/**
 * Computes the default maxLoadSize: half of quantity, rounded up to the nearest step.
 *
 * Example: quantity=11, step=0.5 → 5.5  (ceil(5.5, 0.5) = 5.5)
 * Example: quantity=10.5, step=0.1 → 5.3 (ceil(5.25, 0.1) = 5.3)
 */
export const computeDefaultMaxLoadSize = (quantity: number, step: number): number => {
  return ceilToStep(quantity / 2, step)
}

/**
 * Checks whether balance load is eligible for the given quantity.
 * Does not compute the actual range — call computeLoadBalanceRange for that.
 *
 * min is inclusive: an order of exactly min m³ can still be split between two trucks.
 */
export const isLoadBalanceEligible = (
  config: LoadBalanceConfiguration['loadBalance'],
  quantity: number
): boolean => {
  if (!config.isEnabled) {
    return false
  }

  return (
    quantity >= config.eligibleQuantityRange.min && quantity <= config.eligibleQuantityRange.max
  )
}

/**
 * Computes the valid range for maxLoadSize given quantity and truck geometry.
 * Call isLoadBalanceEligible first — this function assumes eligibility.
 * Returns undefined only when the geometry is bad (truckSize < half quantity).
 *
 * Range semantics:
 * - min: ceil(quantity / 2, step)  — smallest balanced first-truck value
 *                                    (Truck 1 ≥ half, so Truck 2 ≤ Truck 1)
 * - max: truckSize                 — first truck can be at most one full truck
 */
export const computeLoadBalanceRange = (params: {
  quantity: number
  truckSize: number
  quantityStep: number
}): {min: number; max: number} | undefined => {
  const {quantity, truckSize, quantityStep} = params
  const decimals = getNumberOfDecimals(quantityStep)
  const rangeMin = ceilToStep(quantity / 2, quantityStep)
  const rangeMax = roundToDecimalsWithPow(truckSize, decimals)
  if (rangeMin > rangeMax) {
    return undefined
  }
  return {min: rangeMin, max: rangeMax}
}

/**
 * Computes the truck split for the order values.
 * Returns undefined when balance load was not active for the original order.
 * Intended to be called once on mount with stable default values.
 */
export const computeInitialSplit = (params: {
  config: LoadBalanceConfiguration['loadBalance']
  defaultQuantity: number
  truckSize: number
  step: number
  defaultMaxLoadSize: number | undefined
}): {firstTruck: number; secondTruck: number} | undefined => {
  const {config, defaultQuantity, truckSize, step, defaultMaxLoadSize} = params
  if (!isLoadBalanceEligible(config, defaultQuantity)) {
    return undefined
  }

  const range = computeLoadBalanceRange({quantity: defaultQuantity, truckSize, quantityStep: step})
  if (!range) {
    return undefined
  }

  const rangeDefault = computeDefaultMaxLoadSize(defaultQuantity, step)
  return computeSplit(defaultMaxLoadSize ?? rangeDefault, defaultQuantity, step)
}

/**
 * Computes the first/second truck load sizes given maxLoadSize and quantity,
 * rounded to the decimal precision of the quantity step.
 * Returns undefined when maxLoadSize is undefined, zero, or not less than quantity.
 */
export const computeSplit = (
  maxLoadSize: number | undefined,
  quantity: number,
  step: number
): {firstTruck: number; secondTruck: number} | undefined => {
  if (!maxLoadSize || maxLoadSize <= 0 || maxLoadSize >= quantity) return undefined
  const decimals = getNumberOfDecimals(step)
  return {
    firstTruck: roundToDecimalsWithPow(maxLoadSize, decimals),
    secondTruck: roundToDecimalsWithPow(quantity - maxLoadSize, decimals)
  }
}
